import { chapterConfig, type Chapter } from "./chapters";
import { lookup } from "./lookup";
import { PARTY_MAX } from "./party";
/**
 * Slug → human-readable event metadata. Single source of truth for
 * the title/date label shown in surfaces that don't render the full
 * /events config (the admin dashboard, the backend's email templates,
 * any future Slack notifier).
 *
 * The public /events page reads its own richer `eventsConfig.ts` for
 * UI-only fields (long copy, CTA wiring). Both lists are kept in
 * lockstep: when a new event ships, add a row here AND append to the
 * web's eventsConfig.
 */
export interface EventCatalogVenue {
  name: string;
  hall?: string;
  suburb?: string;
  /** A google.com/maps URL — reminders link to it. */
  mapUrl?: string;
}

/** Kept as an alias so existing imports keep compiling; new code uses Chapter. */
export type EventChapter = Chapter;

/**
 * Which city an in-person event belongs to. Drives the location grouping
 * on /events and the location filter in the admin roster. Online and
 * recurring sessions omit it — they belong to no single city.
 */
export type EventLocation = "sydney" | "canberra" | "melbourne" | "new-zealand";

/** Display order for location groupings — biggest chapter first. */
export const EVENT_LOCATIONS: { id: EventLocation; label: string }[] = [
  { id: "sydney", label: "Sydney" },
  { id: "canberra", label: "Canberra" },
  { id: "melbourne", label: "Melbourne" },
  { id: "new-zealand", label: "New Zealand" },
];

/** Human label for a location, with a catch-all for events with no city. */
export function eventLocationLabel(
  id: EventLocation | null | undefined,
): string {
  return EVENT_LOCATIONS.find((l) => l.id === id)?.label ?? "Elsewhere";
}

/**
 * A bookable tier for a paid event. The backend prices the Stripe
 * checkout from this — never from anything the browser sends — so tier
 * ids are the only thing a client may choose; the amount is authoritative
 * here.
 */
export interface EventCatalogTier {
  /** Stable id sent in the checkout request + written to Stripe metadata. */
  id: string;
  /** Short label, e.g. "Full Kund". */
  label: string;
  /** Price of one unit of this tier, in cents. */
  amountCents: number;
  /** How many people one unit of this tier seats. */
  seats: number;
  /** Product name shown on the Stripe checkout line. */
  productName: string;
  /** Max units of this tier one booking may take. Defaults to 1. */
  maxQuantity?: number;
  /**
   * How many physical kunds one booking of this tier occupies, for the
   * "kunds facilitated" tally on a Kund-based Yajna. A Shared Kund is
   * 0.5 (two families share one), a Full Kund / front-row is 1. Omitted
   * on events that aren't kund-based (the tally then doesn't render).
   */
  kunds?: number;
  /**
   * A group of at least this many people on one unit of this tier takes a
   * second Kund, and can split across the two. Australia's Full Kund splits
   * from 6, now that no one shares a Kund. Omit where a group always stays
   * on one Kund.
   */
  twoKundsFrom?: number;
  /**
   * Max bookings of this tier across the whole event. Enforced server-side
   * for every booking — online checkout and the desk alike. Omit for no
   * limit.
   */
  capacity?: number;
  /**
   * Close this tier to the public even while capacity remains — the spots
   * left are held for bookings the team records by hand (bank transfer).
   * The desk ignores this and is gated by  alone.
   */
  soldOut?: boolean;
}

/** An optional paid extra for an event (e.g. a Prasad thali). */
export interface EventCatalogAddOn {
  id: string;
  label: string;
  amountCents: number;
  productName: string;
  /** Max quantity per booking. Defaults to 10. */
  maxQuantity?: number;
}

export interface EventCatalogPrice {
  /** ISO currency, lowercase (e.g. "aud"). */
  currency: string;
  /** Bookable tiers, cheapest first. A simple paid event has just one. */
  tiers: EventCatalogTier[];
  /** Optional paid extras offered alongside the booking. */
  addOns?: EventCatalogAddOn[];
  /**
   * Total physical kunds available, for a Kund-based Yajna. Set alongside
   * per-tier `kunds` weights to light up the "N of <kundsTotal> kunds
   * facilitated" tally in the admin overview. Omitted on other events.
   */
  kundsTotal?: number;
}

/**
 * Optional donation offered on every event's register flow and on the
 * standalone /donate page. The amount is the one figure a client sets, so
 * the backend clamps it to [minCents, maxCents] and rounds to whole cents.
 */
export const DONATION = {
  currency: "aud",
  /** Quick-pick amounts, in cents. */
  presetsCents: [500, 1000, 2500],
  /** Guard rails for the free-text amount. */
  minCents: 100,
  maxCents: 1_000_000,
  productName: "Donation — Vihangam Yog Australia",
} as const;

export interface EventCatalogEntry {
  slug: string;
  title: string;
  /** Display string formatted once so consumers don't recompute. */
  dateLabel: string;
  /** Time-of-day label, e.g. "6:00 — 8:00 pm AEST". */
  timeLabel?: string;
  /** ISO start, when known. Empty for weekly recurring rows. */
  startsAt?: string;
  /** ISO end, when known. Lets consumers tell finished from running. */
  endsAt?: string;
  /** Where it's held — reminders read this so they don't depend on the
   *  web config the backend can't see. */
  venue?: EventCatalogVenue;
  /** Which chapter site owns this event. Defaults to "aus" when
   *  omitted. Lets consumers build the right domain and feature only
   *  their own chapter's upcoming sessions. */
  chapter?: EventChapter;
  /** City this event is held in. Omitted for online / recurring
   *  sessions that belong to no single city. */
  location?: EventLocation;
  /** Present for paid events — the backend reads it to price the Stripe
   *  checkout. Free events omit it. */
  price?: EventCatalogPrice;
  /**
   * The public can no longer book or register interest: the backend refuses
   * both and the page says bookings have closed. The team can still add a
   * booking by hand at the desk.
   */
  bookingsClosed?: boolean;
  /**
   * A meal is served: checking a booking in emails it one meal coupon per
   * person, once.
   */
  mealCoupons?: boolean;
  /**
   * Opt-in per event: ask registrants for dietary requirements. Only
   * worth asking where we're serving a meal, so it's off by default —
   * an extra optional field on every form is friction we don't need.
   *
   * The backend treats this as authoritative: it only stores the answer
   * for events that opted in, so a hand-crafted request can't attach
   * free text to an event that never asked.
   */
  collectsDietaryRequirements?: boolean;
  /**
   * Why we're asking, shown under the field. People answer a personal
   * question more readily when the reason is on screen, and the reason
   * differs per event — so it travels with the opt-in rather than being
   * hardcoded in the form.
   */
  dietaryRequirementsNote?: string;
  /**
   * Opt-out per event: hide the children count on the ticket form.
   * Most gatherings are family events and the kitchen plans around the
   * split, so the field is on by default; an event aimed squarely at
   * adults (a young-adults panel night, say) sets this to false and the
   * form asks for one people count instead.
   */
  collectsChildrenCount?: boolean;
  /**
   * Opt-in per event: invite registrants to drop a question for the
   * panel, with an anonymity choice. Only meaningful for panel-format
   * events, so it's off by default.
   */
  collectsPanelQuestions?: boolean;
  /**
   * "Please arrive by 8:30 am" - the time the doors need people seated,
   * as distinct from `timeLabel` (when the programme runs). Organisers
   * repeat this line to every registrant, so it lives here and reaches
   * the form, the thanks page and the confirmation email together.
   */
  arriveByLabel?: string;
  /** "About 3 hours" - how long people should plan to stay. */
  durationLabel?: string;
  /**
   * The running order for the day, as the organisers publish it. Shown on
   * the events page and the register cards; each row is a time and what
   * happens then.
   */
  agenda?: readonly { time: string; label: string }[];
  liveQuestionsEnabled?: boolean;
}

/** Whether an event asks registrants for dietary requirements. */
export function eventCollectsDietaryRequirements(slug: string): boolean {
  return findEventEntry(slug)?.collectsDietaryRequirements === true;
}

/** Whether an event's ticket form asks for a children count. On by
 *  default; events that opt out take a single people count. */
export function eventCollectsChildrenCount(slug: string): boolean {
  return findEventEntry(slug)?.collectsChildrenCount !== false;
}

/** The reason shown under the dietary field, if the event gives one. */
export function eventDietaryRequirementsNote(slug: string): string | undefined {
  const entry = findEventEntry(slug);
  return entry?.collectsDietaryRequirements
    ? entry.dietaryRequirementsNote
    : undefined;
}

/** Max length of a dietary-requirements note, shared by client + server. */
export const DIETARY_REQUIREMENTS_MAX_LENGTH = 300;

/** Whether an event invites a question for its panel at registration. */
export function eventCollectsPanelQuestions(slug: string): boolean {
  return findEventEntry(slug)?.collectsPanelQuestions === true;
}

/** Max length of a panel question, shared by client + server. */
export const PANEL_QUESTION_MAX_LENGTH = 500;

export const eventCatalog: EventCatalogEntry[] = [
  {
    slug: "11-day-meditation-challenge",
    title: "The 11-Day Meditation Challenge",
    dateLabel: "Sun 14 Jun — Wed 24 Jun 2026",
    startsAt: "2026-06-14T00:00:00.000Z",
    endsAt: "2026-06-25T00:00:00.000Z",
  },
  {
    slug: "sunday-meetup",
    title: "Meditation for Inner Peace — Sunday Meetup",
    dateLabel: "Every Sunday",
    timeLabel: "10:00 am AEST",
  },
  {
    slug: "castle-hill-jun-21",
    title: "Castle Hill — Meditation for Inner Peace",
    dateLabel: "Sunday 21 June 2026",
    timeLabel: "3:30 — 4:30 pm AEST",
    startsAt: "2026-06-21T05:30:00.000Z",
    endsAt: "2026-06-21T06:30:00.000Z",
    location: "sydney",
    venue: {
      name: "George Bell Pavilion, Castle Hill Showground",
      hall: "Doran Drive, Castle Hill NSW 2154",
      suburb: "The Hills Shire",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=George+Bell+Pavilion+Castle+Hill+Showground+Doran+Drive+Castle+Hill+2154",
    },
  },
  {
    slug: "marsden-park-jul-11",
    title: "Vihangam Yog · Meditation for Inner Peace",
    dateLabel: "Saturday 11 July 2026",
    timeLabel: "6:00 — 8:00 pm AEST",
    startsAt: "2026-07-11T08:00:00.000Z",
    endsAt: "2026-07-11T10:00:00.000Z",
    location: "sydney",
    venue: {
      name: "Australian Hindu Multicultural Association (AHMA)",
      hall: "1050 Richmond Road, Marsden Park NSW",
      suburb: "Blacktown City",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Australian+Hindu+Multicultural+Association+1050+Richmond+Road+Marsden+Park+NSW",
    },
  },
  {
    slug: "beaumont-hills-jul-26",
    title: "Beaumont Hills — Community Meditation",
    dateLabel: "Sunday 26 July 2026",
    timeLabel: "3:30 — 4:30 pm AEST",
    startsAt: "2026-07-26T05:30:00.000Z",
    endsAt: "2026-07-26T06:30:00.000Z",
    location: "sydney",
    venue: {
      name: "Beaumont Hills Community Centre",
      hall: "Main Hall",
      suburb: "Beaumont Hills, NSW",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Beaumont+Hills+Community+Centre+Beaumont+Hills+NSW+2155",
    },
  },
  {
    slug: "community-leaders-workshop-sep-18",
    title: "Community Leaders Workshop · Meditation & Inner Balance",
    dateLabel: "Friday 18 September 2026",
    timeLabel: "7:00 — 9:00 pm AEST",
    startsAt: "2026-09-18T09:00:00.000Z",
    endsAt: "2026-09-18T11:00:00.000Z",
    location: "sydney",
    venue: {
      name: "Federation Pavilion Hall, Castle Hill Showground",
      hall: "Doran Drive, Castle Hill NSW 2154",
      suburb: "The Hills Shire",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Federation+Pavilion+Castle+Hill+Showground+Doran+Drive+Castle+Hill+2154",
    },
  },
  {
    slug: "sydney-sep-20",
    title: "Divine Discourse & Meditation",
    dateLabel: "Sunday 20 September 2026",
    timeLabel: "10:30 am — 2:00 pm AEST",
    startsAt: "2026-09-20T00:30:00.000Z",
    endsAt: "2026-09-20T04:00:00.000Z",
    location: "sydney",
    // A meal is served, so the kitchen needs a head count of dietary
    // needs. The only event currently asking.
    collectsDietaryRequirements: true,
    dietaryRequirementsNote:
      "We serve Mahaprasad, a full vegetarian meal, together at the close. Let us know if there's anything we should cook around.",
    venue: {
      name: "Harvey Lowe Pavilion",
      hall: "Castle Hill Showground",
      suburb: "Castle Hill NSW 2154",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Harvey+Lowe+Pavilion+Castle+Hill+Showground+Castle+Hill+NSW+2154",
    },
  },
  {
    slug: "canberra-sep-15",
    title: "Divine Discourse & Meditation",
    dateLabel: "Tuesday 15 September 2026",
    timeLabel: "6:00 — 9:00 pm AEST",
    startsAt: "2026-09-15T08:00:00.000Z",
    endsAt: "2026-09-15T11:00:00.000Z",
    location: "canberra",
    venue: {
      name: "HTCC Shantiniketan Facility",
      hall: "81 Ratcliffe Crescent",
      suburb: "Florey ACT 2615",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=HTCC+Shantiniketan+81+Ratcliffe+Crescent+Florey+ACT+2615",
    },
  },
  {
    slug: "regents-park-sep-21",
    title: "Divine Discourse & Meditation",
    dateLabel: "Monday 21 September 2026",
    timeLabel: "6:00 — 9:00 pm AEST",
    startsAt: "2026-09-21T08:00:00.000Z",
    endsAt: "2026-09-21T11:00:00.000Z",
    location: "sydney",
    venue: {
      name: "Shirdi Sai Temple (Sai Mandir Sydney)",
      hall: "25 Rose Crescent",
      suburb: "Regents Park NSW 2143",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Sai+Mandir+Sydney+25+Rose+Crescent+Regents+Park+NSW+2143",
    },
  },
  {
    slug: "hawan-yajna-sep-19",
    bookingsClosed: true,
    mealCoupons: true,
    title: "51 Kunds Hawan Yajna",
    dateLabel: "Saturday 19 September 2026",
    timeLabel: "10:00 am — 1:30 pm AEST",
    startsAt: "2026-09-19T00:00:00.000Z",
    endsAt: "2026-09-19T03:30:00.000Z",
    location: "sydney",
    venue: {
      name: "Sri Venkateswara Temple",
      hall: "1 Temple Road",
      suburb: "Helensburgh NSW 2508",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Sri+Venkateswara+Temple+1+Temple+Road+Helensburgh+NSW+2508",
    },
    price: {
      currency: "aud",
      kundsTotal: 51,
      tiers: [
        {
          id: "paired-half",
          label: "Paired place",
          amountCents: 12500,
          seats: 1,
          kunds: 0.25,
          productName:
            "51 Kunds Hawan Yajna — a paired place on a shared Kund side",
        },
        {
          id: "shared-group",
          label: "Shared Kund",
          amountCents: 25000,
          seats: 4,
          kunds: 0.5,
          productName:
            "51 Kunds Hawan Yajna — a group's space in a shared Kund",
        },
        {
          id: "full-kund",
          label: "Full Kund",
          amountCents: 50000,
          seats: 8,
          kunds: 1,
          twoKundsFrom: 6,
          capacity: 12,
          soldOut: true,
          productName: "51 Kunds Hawan Yajna — a private Kund for your group",
        },
        {
          id: "front-row",
          label: "Mukhya Yajman",
          amountCents: 100000,
          seats: 8,
          kunds: 1,
          capacity: 5,
          productName:
            "51 Kunds Hawan Yajna — Mukhya Yajman, a Kund at the front",
        },
      ],
      addOns: [
        {
          id: "thali-fruit",
          label: "Puja Thali with fruit",
          amountCents: 2500,
          productName: "Puja Thali with fruit, offered as Prasad",
        },
      ],
    },
  },
  {
    slug: "mordialloc-sep-06",
    title: "Interactive Session with Youth",
    dateLabel: "Sunday 6 September 2026",
    timeLabel: "6:00 \u2014 8:00 pm AEST",
    // 6:00 pm AEST (UTC+10; Victoria is still on standard time in early Sept).
    startsAt: "2026-09-06T08:00:00.000Z",
    endsAt: "2026-09-06T10:00:00.000Z",
    location: "melbourne",
    venue: {
      name: "Aumsai Sansthan Temple",
      hall: "12 Mechanics Lane",
      suburb: "Mordialloc VIC 3195",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Aumsai+Sansthan+Temple+12+Mechanics+Lane+Mordialloc+VIC+3195",
    },
  },
  {
    slug: "melbourne-sep-11",
    title: "Vihangam Yog & Its Science",
    dateLabel: "Friday 11 September 2026",
    timeLabel: "6:30 \u2014 8:30 pm AEST",
    // 6:30 pm AEST (UTC+10; Victoria is still on standard time in early Sept).
    startsAt: "2026-09-11T08:30:00.000Z",
    endsAt: "2026-09-11T10:30:00.000Z",
    location: "melbourne",
    venue: {
      name: "Zee Cheng Khor Moral Uplifting Society",
      suburb: "Melbourne VIC",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Zee+Cheng+Khor+Moral+Uplifting+Society+Melbourne+VIC",
    },
  },
  {
    slug: "deanside-sep-10",
    title: "Divine Discourse & Meditation",
    dateLabel: "Thursday 10 September 2026",
    timeLabel: "5:00 \u2014 8:00 pm AEST",
    // 5:00 pm AEST (UTC+10; Victoria is still on standard time in early Sept).
    startsAt: "2026-09-10T07:00:00.000Z",
    endsAt: "2026-09-10T10:00:00.000Z",
    location: "melbourne",
    venue: {
      name: "Sri Durga Temple",
      hall: "705\u2013715 Neale Road",
      suburb: "Deanside VIC 3336",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Sri+Durga+Temple+705+Neale+Road+Deanside+VIC+3336",
    },
  },
  {
    slug: "rockbank-sep-12",
    title: "108 Kunds Yajna",
    dateLabel: "Saturday 12 September 2026",
    timeLabel: "10:00 am — 1:30 pm AEST",
    startsAt: "2026-09-12T00:00:00.000Z",
    endsAt: "2026-09-12T03:30:00.000Z",
    location: "melbourne",
    venue: {
      name: "Sri Durga Temple",
      hall: "705–715 Neale Road",
      suburb: "Deanside VIC 3336",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Sri+Durga+Temple+705+Neale+Road+Deanside+VIC+3336",
    },
  },
  {
    slug: "but-why-sep-17",
    liveQuestionsEnabled: true,
    title: "But, why?",
    dateLabel: "Thursday 17 September 2026",
    timeLabel: "6:30 — 9:00 pm AEST",
    startsAt: "2026-09-17T08:30:00.000Z",
    endsAt: "2026-09-17T11:00:00.000Z",
    location: "sydney",
    // Dinner is served, and the night is aimed at young adults —
    // one people count, no children column.
    collectsDietaryRequirements: true,
    dietaryRequirementsNote:
      "Dinner is included. Let us know if there's anything we should cook around.",
    collectsChildrenCount: false,
    collectsPanelQuestions: true,
    venue: {
      name: "Rebellion Room, Pioneer Theatre",
      hall: "Castle Grand, 14 Pennant Street, Castle Hill NSW 2154",
      suburb: "A few minutes' walk from Castle Hill Metro",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Castle+Grand+14+Pennant+Street+Castle+Hill+NSW+2154",
    },
  },
  // ─── New Zealand chapter ─────────────────────────────────────────
  {
    slug: "christchurch-sep-26",
    title: "Divine Discourse & Meditation",
    dateLabel: "Saturday 26 September 2026",
    timeLabel: "5:30 — 8:30 pm NZST",
    startsAt: "2026-09-26T05:30:00.000Z",
    endsAt: "2026-09-26T08:30:00.000Z",
    chapter: "nz",
    location: "new-zealand",
    venue: {
      name: "64 Kirk Road",
      suburb: "Templeton, Christchurch 8042",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=64+Kirk+Road+Templeton+Christchurch+8042",
    },
  },
  // ─── Singapore chapter ───────────────────────────────────────────
  // Facts marked "organiser" are owed by the Singapore team (spec §9);
  // each is a one-line edit here.
  {
    slug: "sg-stage-programme-oct-03",
    title:
      "Vihangam Yoga Religious Discourse, Prayer, Devotional Activities and Spiritual Meditation",
    dateLabel: "Saturday 3 October 2026",
    // Times from the organisers, 10 Sep 2026 (SGT is UTC+8).
    timeLabel: "4:00 pm to 8:00 pm, followed by dinner",
    startsAt: "2026-10-03T08:00:00.000Z",
    endsAt: "2026-10-03T12:00:00.000Z",
    arriveByLabel: "4:00 pm for registration",
    durationLabel: "until 8:00 pm, then dinner",
    agenda: [
      { time: "4:00 pm", label: "Registration" },
      {
        time: "4:30 pm",
        label: "Welcome note, Bhajan, Stage Programme, Award Ceremony",
      },
      { time: "6:00 pm", label: "Meditation" },
      { time: "7:00 pm", label: "Swamiji's Discourse" },
      { time: "8:00 pm", label: "Dinner" },
    ],
    chapter: "sg",
    // Venue per the organisers' visit flyer (9 Sep 2026).
    venue: {
      name: "Quantum Arena, OWIS",
      suburb: "Punggol",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=One+World+International+School+Punggol+Singapore",
    },
    collectsChildrenCount: true,
  },
  {
    slug: "sg-hawan-yajna-oct-04",
    title: "501-Kunds Vedic Hawan Yajna",
    dateLabel: "Sunday 4 October 2026",
    // Times from the organisers, 10 Sep 2026 (SGT is UTC+8).
    timeLabel: "9:00 am to 11:00 am, followed by lunch",
    startsAt: "2026-10-04T01:00:00.000Z",
    endsAt: "2026-10-04T03:30:00.000Z",
    arriveByLabel: "8:00 am for registration",
    durationLabel: "until 11:30 am, lunch included",
    agenda: [
      { time: "8:00 am", label: "Registration" },
      { time: "8:30 am", label: "Sitting and safety instructions" },
      { time: "9:00 am", label: "Hawan instructions" },
      { time: "9:15 am", label: "Hawan starts" },
      { time: "11:00 am", label: "Hawan finishes, Darshan of Swamiji" },
      { time: "11:30 am", label: "Lunch" },
    ],
    chapter: "sg",
    // The Yajna is on the open field beside Blk 118, not in the block
    // itself (organisers, 9 Sep 2026). The pin is the field.
    venue: {
      name: "Open field on Aljunied Ave 1",
      suburb: "beside Blk 118, Aljunied Ave 2",
      mapUrl: "https://www.google.com/maps/search/?api=1&query=1.3195,103.8882",
    },
    collectsChildrenCount: true,
    price: {
      currency: "sgd",
      // Prices per the organisers (9 Sep 2026). A person 16 or over pays
      // S$51 for a place on a shared Kund; a family may take a whole Kund
      // for S$201 (up to 5 people, any age). Under-16s are free on the
      // shared tier but are still listed so food can be counted. The
      // Yajna has 501 Kunds; bundle.ts refuses a booking past that.
      kundsTotal: 501,
      tiers: [
        {
          id: "shared",
          label: "Shared Kund, S$51 per person",
          amountCents: 5100,
          seats: 1,
          kunds: 0.2,
          // Both caps follow PARTY_MAX, so a party the form accepts is
          // never refused at pricing for being too large.
          maxQuantity: PARTY_MAX,
          productName:
            "Hawan Yajna 4 Oct 2026, shared Kund, one person (16 and over)",
        },
        {
          id: "kund",
          label: "Your own Kund, S$201 per Kund (per family, not shared)",
          amountCents: 20100,
          seats: 5,
          kunds: 1,
          maxQuantity: Math.ceil(PARTY_MAX / 5),
          productName: "Hawan Yajna 4 Oct 2026, one Kund (up to 5 people)",
        },
      ],
    },
  },
  {
    slug: "sg-kids-meditation-weekly",
    title: "Kids' (Online) Meditation",
    dateLabel: "Every Sunday",
    timeLabel: "9:00 am SGT",
    chapter: "sg",
  },
  {
    slug: "sg-weekly-satsang",
    title: "Weekly Satsang",
    dateLabel: "Every Sunday",
    timeLabel: "11:00 am SGT",
    chapter: "sg",
  },
];

/**
 * Retired slugs that must keep resolving because they are printed on
 * flyers, QR codes or short links already out in the world. We can't
 * recall a printed flyer, so the alias stays as long as the artefact
 * might be in someone's hands.
 *
 * rockbank-sep-12 is canonical (it's what the Melbourne flyer's QR
 * encodes); bit.ly/sdtsep12 was minted against the older slug and
 * Bitly's free plan won't let us repoint it.
 */
export const EVENT_SLUG_ALIASES = {
  "durga-yajna-sep-12": "rockbank-sep-12",
  // An early cut of the Deanside flyer printed sep-11 while the event
  // is on Thursday the 10th. The flyer has been corrected, but any copy
  // already shared keeps working.
  "deanside-sep-11": "deanside-sep-10",
} satisfies Readonly<Record<string, string>>;

/** Resolves an alias to its canonical slug; passes others through. */
export function canonicalEventSlug(slug: string): string {
  return lookup(EVENT_SLUG_ALIASES, slug) ?? slug;
}

export function findEventEntry(slug: string): EventCatalogEntry | undefined {
  const canonical = canonicalEventSlug(slug);
  return eventCatalog.find((e) => e.slug === canonical);
}

/** The chapter that owns an event, defaulting to "aus" when unset. */
export function eventChapterOf(entry: EventCatalogEntry): EventChapter {
  return entry.chapter ?? "aus";
}

/** The catalog as one chapter's Worker or site sees it. */
export function catalogForChapter(chapter: Chapter): EventCatalogEntry[] {
  return eventCatalog.filter((e) => eventChapterOf(e) === chapter);
}

/**
 * Dated, still-upcoming catalog events, soonest first. Used by the
 * post-event follow-up to feature "where to see us next". Filters to a
 * single chapter so a Sydney attendee isn't pointed at a Christchurch
 * session, and drops any given slug (usually the event just attended).
 * Weekly recurring rows (no startsAt) are excluded — the follow-up
 * points at concrete dates.
 */
export function upcomingEvents(opts?: {
  now?: Date;
  chapter?: EventChapter;
  excludeSlug?: string;
  limit?: number;
}): EventCatalogEntry[] {
  const now = (opts?.now ?? new Date()).getTime();
  const chapter = opts?.chapter ?? "aus";
  const list = eventCatalog
    .filter((e) => eventChapterOf(e) === chapter)
    .filter((e) => e.slug !== opts?.excludeSlug)
    .filter((e) => e.startsAt && new Date(e.startsAt).getTime() > now)
    .sort(
      (a, b) =>
        // SAFETY: this comparator sorts the dated list, filtered to rows with startsAt just above.
        new Date(a.startsAt as string).getTime() -
        // SAFETY: this comparator sorts the dated list, filtered to rows with startsAt just above.
        new Date(b.startsAt as string).getTime(),
    );
  return opts?.limit ? list.slice(0, opts.limit) : list;
}

/** IANA zone for a chapter's local calendar, from its chapter config (so
 *  Singapore events are judged in Singapore time, not Sydney's). */
export function chapterTimeZone(chapter: EventChapter): string {
  return chapterConfig(chapter).timezone;
}

/** "YYYY-MM-DD" of an instant in a zone (Intl, no library). */
export function localDateKey(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Catalog events happening on `now`'s calendar date, judged in each
 * event's own chapter zone — the reception desk's "today". Undated
 * (weekly) rows never match.
 */
export function eventsOnDate(
  entries: EventCatalogEntry[],
  now: Date,
): EventCatalogEntry[] {
  const nowIso = now.toISOString();
  return entries.filter((e) => {
    if (!e.startsAt) {
      return false;
    }
    const zone = chapterTimeZone(eventChapterOf(e));
    return localDateKey(e.startsAt, zone) === localDateKey(nowIso, zone);
  });
}
